import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Moon, Sun } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../../assets/images/logos.png';
import { trackGoal, trackEvent, GOALS } from '../../utils/yandexMetrika';
import './Header.css';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [activeSection, setActiveSection] = useState('');
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Определяем активную секцию только на главной странице
      if (isHomePage) {
        const sections = ['services', 'promo', 'reviews', 'form'];
        const scrollPosition = window.scrollY + 100;

        let currentSection = '';

        for (const section of sections) {
          const element = document.getElementById(section);
          if (element) {
            const { offsetTop, offsetHeight } = element;
            if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
              currentSection = section;
              break;
            }
          }
        }

        // Проверяем footer-contacts отдельно
        const footerElement = document.getElementById('footer-contacts');
        if (footerElement) {
          const { offsetTop } = footerElement;
          if (scrollPosition >= offsetTop - 200) {
            currentSection = 'footer-contacts';
          }
        }

        setActiveSection(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);

    if (isHomePage) {
      // Если мы на главной странице, просто прокручиваем
      scrollToSection(sectionId);
    } else {
      // Если мы на другой странице, переходим на главную с хэшем
      navigate(`/#${sectionId}`);
    }
  };

  const scrollToSection = (sectionId) => {
    setTimeout(() => {
      const section = document.getElementById(sectionId);
      if (section) {
        const headerHeight = 80;
        const elementPosition = section.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - headerHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  // Обработка хэша при загрузке страницы
  useEffect(() => {
    if (location.hash && isHomePage) {
      const sectionId = location.hash.replace('#', '');
      scrollToSection(sectionId);
    }
  }, [location, isHomePage]);

  const handleLogoClick = (e) => {
    if (isHomePage) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      setLogoClickCount(prev => prev + 1);
      if (logoClickCount >= 6) {
        const logoElement = e.currentTarget;
        logoElement.classList.add('rainbow');
        setTimeout(() => {
          logoElement.classList.remove('rainbow');
        }, 2000);
        setLogoClickCount(0);
      }
    }
  };

  const handlePhoneClick = () => {
    trackGoal(GOALS.PHONE_CLICK, { phone_location: 'header' });
    trackEvent('Contact', 'Phone Click', 'Header');
  };

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="container">
        <div className="header-content">
          <Link 
            to="/" 
            className="logo"
            onClick={handleLogoClick}
          >
            <img src={logo} alt="Мигрант PRO" className="logo-img" />
          </Link>

          <nav className={`nav ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
            <a 
              href="/#services" 
              onClick={(e) => handleNavClick(e, 'services')}
              className={activeSection === 'services' ? 'active' : ''}
            >
              Услуги
            </a>
            <a 
              href="/#promo" 
              onClick={(e) => handleNavClick(e, 'promo')}
              className={activeSection === 'promo' ? 'active' : ''}
            >
              Акции
            </a>
            <a 
              href="/#reviews" 
              onClick={(e) => handleNavClick(e, 'reviews')}
              className={activeSection === 'reviews' ? 'active' : ''}
            >
              Отзывы
            </a>
            <a 
              href="/#footer-contacts" 
              onClick={(e) => handleNavClick(e, 'footer-contacts')}
              className={activeSection === 'footer-contacts' ? 'active' : ''}
            >
              Контакты
            </a>
            <a 
              href="/#form" 
              className="nav-cta" 
              onClick={(e) => handleNavClick(e, 'form')}
            >
              Оставить заявку
            </a>
          </nav>

          <a 
            href="tel:+79257166607" 
            className="phone-link"
            onClick={handlePhoneClick}
          >
            <Phone size={20} />
            <span>+7 (925) 716-66-07</span>
          </a>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button 
              onClick={toggleTheme} 
              className="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
            </button>

            <button 
              className="mobile-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;